import { useState, useCallback } from 'react';
import axios from 'axios';
import { Calculator, Flame, Apple, Droplet, Heart, Activity, Target, Loader2, ChevronRight, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

import { API } from '../config/api';

const ACTIVITY_LEVELS = [
  { id: 'sedentary', name: 'Sedentary', desc: 'Little or no exercise' },
  { id: 'light', name: 'Light', desc: '1-3 days/week' },
  { id: 'moderate', name: 'Moderate', desc: '3-5 days/week' },
  { id: 'active', name: 'Active', desc: '6-7 days/week' },
  { id: 'very_active', name: 'Very Active', desc: 'Hard training' },
  { id: 'athlete', name: 'Athlete', desc: 'Professional level' }
];

const GOALS = [
  { id: 'lose', name: 'Lose Weight', desc: '-500 cal/day', color: 'orange' },
  { id: 'maintain', name: 'Maintain', desc: 'Stay same', color: 'blue' },
  { id: 'gain', name: 'Lean Gain', desc: '+300 cal/day', color: 'green' },
  { id: 'bulk', name: 'Bulk', desc: '+500 cal/day', color: 'purple' }
];

export default function CalorieCalculator({ childData, onSaveGoals }) {
  const [formData, setFormData] = useState({
    age: childData?.age || '',
    weight_kg: childData?.weight || '',
    height_cm: childData?.height || '',
    gender: childData?.gender || 'male',
    activity_level: 'moderate',
    goal: 'maintain',
    sport: childData?.sport || ''
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const calculateGoals = useCallback(async () => {
    if (!formData.age || !formData.weight_kg || !formData.height_cm) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/calorie-calculator/calculate`, formData);
      setResults(res.data);
      setShowDetails(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  }, [formData]);

  const handleSave = () => {
    if (results && onSaveGoals) {
      onSaveGoals(results);
      toast.success('Calorie goals saved!');
    }
  };

  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden" data-testid="calorie-calculator">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500/20 to-red-500/20 p-4 border-b border-zinc-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center">
            <Calculator className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-white">Calorie Goal Calculator</h3>
            <p className="text-xs text-zinc-400">Personalized nutrition targets based on metrics</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Input Form */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Age</label>
            <Input
              type="number"
              value={formData.age}
              onChange={(e) => setFormData(p => ({ ...p, age: e.target.value }))}
              placeholder="13"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Weight (kg)</label>
            <Input
              type="number"
              value={formData.weight_kg}
              onChange={(e) => setFormData(p => ({ ...p, weight_kg: e.target.value }))}
              placeholder="50"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Height (cm)</label>
            <Input
              type="number"
              value={formData.height_cm}
              onChange={(e) => setFormData(p => ({ ...p, height_cm: e.target.value }))}
              placeholder="160"
              className="bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 mb-1">Gender</label>
            <select
              value={formData.gender}
              onChange={(e) => setFormData(p => ({ ...p, gender: e.target.value }))}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="male">Male</option>
              <option value="female">Female</option>
            </select>
          </div>
        </div>

        {/* Activity Level */}
        <div>
          <label className="block text-xs text-zinc-500 mb-2">Activity Level</label>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {ACTIVITY_LEVELS.map(level => (
              <button
                key={level.id}
                onClick={() => setFormData(p => ({ ...p, activity_level: level.id }))}
                className={`p-2 rounded-lg border text-center transition-all ${
                  formData.activity_level === level.id
                    ? 'bg-[#02b5ff]/20 border-[#02b5ff] text-[#02b5ff]'
                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                <div className="text-xs font-medium">{level.name}</div>
                <div className="text-[10px] opacity-70">{level.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Goal */}
        <div>
          <label className="block text-xs text-zinc-500 mb-2">Goal</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {GOALS.map(goal => (
              <button
                key={goal.id}
                onClick={() => setFormData(p => ({ ...p, goal: goal.id }))}
                className={`p-3 rounded-lg border text-center transition-all ${
                  formData.goal === goal.id
                    ? `bg-${goal.color}-500/20 border-${goal.color}-500 text-${goal.color}-400`
                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-400 hover:border-zinc-600'
                }`}
                style={{
                  backgroundColor: formData.goal === goal.id ? `var(--${goal.color}-bg, rgba(255,165,0,0.1))` : undefined,
                  borderColor: formData.goal === goal.id ? `var(--${goal.color}-border, #f97316)` : undefined
                }}
              >
                <div className="text-sm font-medium">{goal.name}</div>
                <div className="text-xs opacity-70">{goal.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Sport (Optional) */}
        <div>
          <label className="block text-xs text-zinc-500 mb-1">Sport (Optional)</label>
          <select
            value={formData.sport}
            onChange={(e) => setFormData(p => ({ ...p, sport: e.target.value }))}
            className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm"
          >
            <option value="">Select sport for optimized macros</option>
            <option value="Football (Soccer)">Football (Soccer)</option>
            <option value="Basketball">Basketball</option>
            <option value="Swimming">Swimming</option>
            <option value="Tennis">Tennis</option>
            <option value="Athletics">Athletics</option>
            <option value="Gymnastics">Gymnastics</option>
            <option value="Rugby">Rugby</option>
          </select>
        </div>

        {/* Calculate Button */}
        <Button
          onClick={calculateGoals}
          disabled={loading}
          className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />}
          Calculate Goals
        </Button>

        {/* Results */}
        {results && showDetails && (
          <div className="space-y-4 pt-4 border-t border-zinc-800">
            {/* Main Calories */}
            <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-xl p-4 border border-orange-500/30">
              <div className="text-center mb-4">
                <div className="text-4xl font-bold text-white">{results.target_calories}</div>
                <div className="text-sm text-orange-400">Daily Calories Target</div>
              </div>
              
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="bg-zinc-800/50 rounded-lg p-2">
                  <div className="text-lg font-bold text-zinc-300">{results.bmr}</div>
                  <div className="text-xs text-zinc-500">BMR (Base)</div>
                </div>
                <div className="bg-zinc-800/50 rounded-lg p-2">
                  <div className="text-lg font-bold text-zinc-300">{results.tdee}</div>
                  <div className="text-xs text-zinc-500">TDEE (Active)</div>
                </div>
              </div>
            </div>

            {/* Macros */}
            <div>
              <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Target className="h-4 w-4 text-[#02b5ff]" />
                Daily Macro Targets
              </h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-center">
                  <Apple className="h-5 w-5 mx-auto text-red-400 mb-1" />
                  <div className="text-xl font-bold text-white">{results.macros.protein_g}g</div>
                  <div className="text-xs text-red-400">Protein ({results.macros.protein_pct}%)</div>
                  <div className="text-[10px] text-zinc-500">{results.protein_per_kg}g/kg</div>
                </div>
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 text-center">
                  <Droplet className="h-5 w-5 mx-auto text-yellow-400 mb-1" />
                  <div className="text-xl font-bold text-white">{results.macros.carbs_g}g</div>
                  <div className="text-xs text-yellow-400">Carbs ({results.macros.carbs_pct}%)</div>
                </div>
                <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-3 text-center">
                  <Heart className="h-5 w-5 mx-auto text-pink-400 mb-1" />
                  <div className="text-xl font-bold text-white">{results.macros.fat_g}g</div>
                  <div className="text-xs text-pink-400">Fat ({results.macros.fat_pct}%)</div>
                </div>
              </div>
            </div>

            {/* Meal Distribution */}
            <div>
              <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Activity className="h-4 w-4 text-green-400" />
                Suggested Meal Distribution
              </h4>
              <div className="grid grid-cols-4 gap-2">
                {Object.entries(results.meal_distribution).map(([meal, cals]) => (
                  <div key={meal} className="bg-zinc-800/50 rounded-lg p-2 text-center">
                    <div className="text-sm font-bold text-white">{cals}</div>
                    <div className="text-xs text-zinc-500 capitalize">{meal}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recommendations */}
            {results.recommendations?.length > 0 && (
              <div className="bg-[#02b5ff]/10 border border-[#02b5ff]/30 rounded-xl p-4">
                <h4 className="text-sm font-medium text-[#02b5ff] mb-2 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Recommendations
                </h4>
                <ul className="space-y-1">
                  {results.recommendations.map((rec, idx) => (
                    <li key={idx} className="text-xs text-zinc-300 flex items-start gap-2">
                      <ChevronRight className="h-3 w-3 text-[#02b5ff] mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Save Button */}
            {onSaveGoals && (
              <Button
                onClick={handleSave}
                className="w-full bg-green-500 hover:bg-green-600"
              >
                Save as Daily Goals
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
